import { useRef, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { useAccount } from 'wagmi';
import { parseEther, parseUnits, formatEther, Hex } from 'viem';
import { useModal } from '~/core/contexts/ModalContext';
import { useAddFunds } from '~/core/providers/AddFundsProvider';
import { useToast } from '~/core/contexts/ToastContext';
import { useSound } from '../components/SoundHandler';
import { useAssets } from '../components/AssetHandler';
import { useBGM } from './useBGM';
import { useGameEngine } from './useGameEngine';
import { useCanvasSetup } from './useCanvasSetup';
import { useAllBalance } from '~/core/hooks/useAllBalance';
import { useMetadata } from '../components/MetadataHandler';
import { profileCache } from '../components/ProfileSlot';
import {
  atomGameState, atomGameMode, atomIsGamePreparing,
  atomLevel, atomAchievedLevel, atomExperience, atomLevelProgress,
  atomBallsRemaining, atomTotalBallsUsed, atomActiveBallCount, atomSpecialHitsCount,
  atomHoneyEarned, atomCollectHoneyTrigger, atomTotalWinEth, atomWageredEth,
  atomRecentBins, atomUiHidden, atomGameTheme, atomShowMachinary,
  atomBoardReady, atomControlsReady, atomEthPrice, atomSessionHits,
} from '../states/game.atoms';
import {
  atomUserInput, atomHardMode, atomIsIgamingDemo, atomDemoBalanceUsd
} from '../states';
import { atomIsReplayMode } from '../states/game.atoms';
import { checkVersionMode } from '../api';
import { atomWalletModal } from '~/blinko/modals/wallet.modal';

import type { BallHitBinEvent, SpecialPinHitEvent } from '../game-engine/events'

import { BIN_MULTIPLIERS, LAUNCHES_PER_GAME, BlinkoMode, levelToMultiplier, computeLevelProgress, LEVEL_CONFIG_V1, LEVEL_CONFIG_V2, LEVEL_CONFIG_HARD_V1, LEVEL_CONFIG_HARD_V2, LevelConfig, BlinkoVersion } from '../game-engine/constants';
import { SETTINGS } from '../constants';
import { ZERO_ADDRESS } from '~/core/constants';

import { getSecureSeed, deriveSourceSeedFromCommitment } from '../utils';
import { seedPlanWorkerClient } from '../seedPlanWorkerClient';
import { generateSeedPlan, generateLevelSeeds } from '../seedPlan';
import { MULTIPLE_PRECISION_DECIMALS } from '~/core/constants';
import { useBlinkoEthPrice } from './useBlinkoEthPrice';
import { BonusStageModal } from '../modals/bonus.round.modal';
import { ShareModal } from '../modals/share.modal';
import { ResultModal } from '../modals/result.modal';
import { useGameService } from './useGameService';
import { useError } from '~/core/contexts/ErrorContext';

export const useGameController = (props?: { physicsSteps?: number; isIgamingDemo?: boolean }) => {
  const [gameState, setGameState] = useAtom(atomGameState);
  const [, setGameMode] = useAtom(atomGameMode);
  const [, setIsGamePreparing] = useAtom(atomIsGamePreparing);

  const [level, setLevel] = useAtom(atomLevel);
  const [, setAchievedLevel] = useAtom(atomAchievedLevel);
  const [experience, setExperience] = useAtom(atomExperience);

  const [ballsRemaining, setBallsRemaining] = useAtom(atomBallsRemaining);
  const [, setTotalBallsUsed] = useAtom(atomTotalBallsUsed);
  const [activeBallCount, setActiveBallCount] = useAtom(atomActiveBallCount);
  const [, setSpecialHitsCount] = useAtom(atomSpecialHitsCount);

  const setHoneyEarned = useSetAtom(atomHoneyEarned);
  const setCollectHoneyTrigger = useSetAtom(atomCollectHoneyTrigger);
  const [totalWinEth, setTotalWinEth] = useAtom(atomTotalWinEth);
  const [, setWageredEth] = useAtom(atomWageredEth);
  const setEthPrice = useSetAtom(atomEthPrice);

  const setLevelProgress = useSetAtom(atomLevelProgress);

  const setRecentBins = useSetAtom(atomRecentBins);
  const setUiHidden = useSetAtom(atomUiHidden);
  const setTheme = useSetAtom(atomGameTheme);
  const setShowMachinary = useSetAtom(atomShowMachinary);
  const setSessionHits = useSetAtom(atomSessionHits);

  const [boardReady] = useAtom(atomBoardReady);
  const [controlsReady] = useAtom(atomControlsReady);

  // Global State
  const { address } = useAccount();
  const { showModal } = useModal();
  const toast = useToast();
  const sound = useSound();
  const { setTheme: setBgmTheme } = useBGM();
  const { markAssetsReady } = useAssets();
  const { mutate: mutateAllBalance, getBalance } = useAllBalance();
  const { mutateAchievements, mutateLeaderboard } = useMetadata();
  const { addFunds } = useAddFunds();

  const [userInput, setUserInput] = useAtom(atomUserInput);
  const [isHardMode, setIsHardMode] = useAtom(atomHardMode);
  const setWalletVisible = useSetAtom(atomWalletModal.visible);
  const isSigned = useAtomValue(atomWalletModal.isSigned);
  // Prefer the prop (known synchronously on the first render) OR-ed with the atom
  // (set post-mount). This avoids a race where the pin canvas is first sized at full
  // viewport width and the engine lays out pins before the demo panel width is known.
  const isIgamingDemo = useAtomValue(atomIsIgamingDemo) || props?.isIgamingDemo === true;
  const setDemoBalance = useSetAtom(atomDemoBalanceUsd);
  const { handleError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derived Logic
  const [gameVersion, setGameVersion] = useState<BlinkoVersion>('v2');
  const [levelConfig, setLevelConfig] = useState<LevelConfig>(LEVEL_CONFIG_V2);
  const betWei = useMemo(() => parseEther(userInput.tollAmount || '0'), [userInput.tollAmount]);
  const isGameActive = gameState !== 'waiting';

  const { priceUsd: ethPriceUsd } = useBlinkoEthPrice({ isGameActive });
  
  useEffect(() => {
    setEthPrice(ethPriceUsd);
  }, [ethPriceUsd, setEthPrice]);

  const { startGame: serviceStartGame, resumeGame: serviceResumeGame, replayGame: serviceReplayGame } = useGameService({
    playerAddress: address ?? null,
    defaultBetAmount: betWei.toString(),
  });

  // Refs
  const hardModeRef = useRef(isHardMode);
  const activeBallCountRef = useRef(0);
  const totalWinEthRef = useRef(0n);
  totalWinEthRef.current = totalWinEth
  const sessionBetAmountRef = useRef(0n);
  const honeyAnimationPlayedLevelsRef = useRef<Set<number>>(new Set());
  const currentModeRef = useRef<BlinkoMode>('main');
  const levelRef = useRef(level);
  const ballsRemainingRef = useRef(ballsRemaining);
  const ballSeedUsageRef = useRef<number>(0);
  const ballSeedPlanRef = useRef<number[]>([]);
  const sourceSeedRef = useRef<number>(0);       // for lazy per-level seed generation
  const lazyLetterSeedRef = useRef<number>(0);
  const planRequestIdRef = useRef(0);
  const startInProgressRef = useRef(false);
  const gameIdRef = useRef<string | null>(null);

  // Sync Refs
  useEffect(() => {
    hardModeRef.current = isHardMode;
  }, [isHardMode]);

  useEffect(() => {
    ballsRemainingRef.current = ballsRemaining;
  }, [ballsRemaining]);

  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  useEffect(() => {
    activeBallCountRef.current = activeBallCount;
  }, [activeBallCount]);

  // Sync Level Progress
  useEffect(() => {
    const progress = computeLevelProgress(level, experience, levelConfig);
    setLevelProgress(progress);
  }, [level, experience, levelConfig, setLevelProgress]);

  // Loading Logic
  useEffect(() => {
    if (boardReady && controlsReady) {
      markAssetsReady();
    }
  }, [boardReady, controlsReady, markAssetsReady]);

  // Helper Logic
  const calculateReachedLevel = (exp: number): number => {
    const cfg = levelConfig;
    let reachedLevel = 1;
    for (let i = 1; i <= cfg.maxLevel; i++) {
      if (i < cfg.maxLevel && exp >= cfg.experienceToLevelUp[i]) {
        reachedLevel = i + 1;
      } else if (i === cfg.maxLevel && exp >= cfg.experienceToLevelUp[i - 1]) {
        reachedLevel = i;
        break;
      }
    }
    return reachedLevel;
  };

  // Optimize win calculation constants
  const winCalcDenominator = useMemo(() => {
    // Denominator = (Launches * Level1Multiplier) * Precision
    // Note: levelToMultiplier(1) is always 1, LAUNCHES_PER_GAME is 10
    const totalSubBalls = BigInt(LAUNCHES_PER_GAME * levelToMultiplier(1));
    const precision = BigInt(10 ** MULTIPLE_PRECISION_DECIMALS);
    return totalSubBalls * precision;
  }, []);

  // Game Engine Handlers
  const handleBallHitBin = (event: BallHitBinEvent) => {
    setActiveBallCount(prev => Math.max(0, prev - 1));
    const bin = event.payload.bin;
    const inBonusStage = currentModeRef.current === 'bonus';
    const isHardMainStage = hardModeRef.current && !inBonusStage;
    const multiplier = isHardMainStage ? '0' : BIN_MULTIPLIERS[bin];

    // Update Recent Bins
    if (multiplier !== '0') {
      setRecentBins(prev => [`${multiplier}`, ...prev].slice(0, 10));
    }

    setSessionHits(prev => [...prev, bin]); // Record for session

    // Play Sound
    const soundKeyMap: Record<number, 'mul1' | 'mul2' | 'mul3' | 'mul4' | 'mul5'> = {
      0: 'mul5', 9: 'mul5',
      1: 'mul4', 8: 'mul4',
      2: 'mul3', 7: 'mul3',
      3: 'mul2', 6: 'mul2',
      4: 'mul1', 5: 'mul1'
    };
    const key = soundKeyMap[bin];
    if (key) {
      sound.play(key);
    }

    // Accumulate Winnings
    const currentBet = sessionBetAmountRef.current;
    if (currentBet > 0n && multiplier !== '0') {
      if (winCalcDenominator > 0n) {
        // Optimized calc using pre-calculated denominator
        const multiplierScaled = parseUnits(multiplier, MULTIPLE_PRECISION_DECIMALS);
        let deltaWin = (currentBet * multiplierScaled) / winCalcDenominator;
        // HARD (reshaped) deliverable bins = NORMAL bins x 1.043 (meanBin fine-tune: RTP 96.13%
        // / ceiling 5994x, CV 15.6). Board renders the base vector; apply the scale to HARD
        // payouts so the live economy matches the published per-mode enumerated stats.
        if (hardModeRef.current) deltaWin = (deltaWin * 10430n) / 10000n;
        setTotalWinEth(prev => prev + deltaWin);
      }
    }
  };

  const handleSpecialPinHit = (event: SpecialPinHitEvent) => {
    const expGained = event.payload.expGained;
    if (expGained) {
      const exp = levelConfig.experiencePerSpecialHit;
      setExperience(prev => {
        const next = prev + exp;
        const reached = calculateReachedLevel(next);
        setAchievedLevel(reached);
        return next;
      });
    }

    setSpecialHitsCount(prev => {
      const next = prev + 1;
      // Honey Logic
      {
        const refBetWei = sessionBetAmountRef.current;
        const minWei = parseEther('0.001');
        if (refBetWei > 0n) {
          const honeyValueBig = (refBetWei * BigInt(next)) / minWei;
          const honeyValue = Number(honeyValueBig);
          setHoneyEarned(honeyValue);

          // Animation trigger
          if (honeyValue > 0) {
            const currentLvl = levelRef.current || 1;
            if (!honeyAnimationPlayedLevelsRef.current.has(currentLvl)) {
              honeyAnimationPlayedLevelsRef.current.add(currentLvl);
              setCollectHoneyTrigger(p => p + 1);
            }
          }
        }
      }
      return next;
    });
  };

  const handleLetterActivated = () => {
    sound.play('mul5');
    setExperience(prev => {
      const next = prev + levelConfig.experiencePerLetter;
      setAchievedLevel(calculateReachedLevel(next));
      return next;
    });
  };

  const handlePlaySound = (event: { payload?: { soundType: string } }) => {
    if (event.payload?.soundType === 'tick') {
      sound.play('tick');
    }
    if (event.payload?.soundType === 'special') {
      sound.play('special');
    }
  };

  const handleModeChange = (mode: BlinkoMode) => {
    currentModeRef.current = mode;
    setGameMode(mode);
    if (mode === 'bonus') {
      showModal(hide => (
        <BonusStageModal
          onTrigger={() => {
            setShowMachinary(true);
            setTheme('night');
            setBgmTheme('night');
          }}
          onDismiss={hide}
        />
      ));
      // Level up logic for bonus
      setExperience(exp => {
        const reached = calculateReachedLevel(exp);
        const nextLevel = Math.max(2, reached);
        setLevel(nextLevel);
        setAchievedLevel(nextLevel);
        return exp;
      });
      setGameState('bonus');
      setBallsRemaining(LAUNCHES_PER_GAME);
      setTotalBallsUsed(0);
      setActiveBallCount(0);
    }
    if (mode === 'main') {
      setActiveBallCount(0);
      setGameState(prev => prev === 'waiting' ? prev : 'playing');
      setTheme('day');
      setBgmTheme('day');
    }
  };

  // Setup Engine — reserve the demo ECONOMY panel width so the pin field scales
  // to the same width as the Rive board (kept in sync with BlinkoGame's right:300).
  const { canvasRef, scale, dimensions } = useCanvasSetup(isIgamingDemo ? 300 : 0);
  const { addBall, initializeLetters, setCharacterState } = useGameEngine({
    scale,
    canvasRef,
    onBallHitBin: handleBallHitBin,
    onSpecialPinHit: handleSpecialPinHit,
    onPlaySound: handlePlaySound,
    onModeChange: handleModeChange,
    onLetterActivated: handleLetterActivated,
    soundPlayer: sound.play,
    level,
    experience,
    // betWei removed as it's no longer needed for rendering
    levelConfig,
    isHardMode: isHardMode,
    version: gameVersion,
    physicsSteps: props?.physicsSteps,
  });

  const applySeedPlan = (plan: { letterSeed: number; ballSeeds: number[] }) => {
    ballSeedUsageRef.current = 0;
    ballSeedPlanRef.current = plan.ballSeeds;
    lazyLetterSeedRef.current = plan.letterSeed;
    initializeLetters(plan.letterSeed);
  };

  // Ensure the lazy seed plan contains every ball for all levels up through `lvl`.
  // generateLevelSeeds uses fixed deterministic offsets, so this is idempotent and only fills
  // MISSING levels — safe to call from any path that raises the level (bonus entry, level-up, or
  // defensively right before a drop). Fixes the bug where a bonus level had no balls because the
  // level was reached via a path (handleModeChange bonus entry) that never generated its seeds.
  const ensureSeedsForLevel = (lvl: number) => {
    for (let L = 2; L <= lvl; L++) {
      const cumulativeThroughL = LAUNCHES_PER_GAME * (2 ** L - 1); // main 10 + bonus levels 2..L
      if (ballSeedPlanRef.current.length < cumulativeThroughL) {
        const lvlSeeds = generateLevelSeeds(gameVersion, sourceSeedRef.current, L, lazyLetterSeedRef.current);
        ballSeedPlanRef.current = [...ballSeedPlanRef.current, ...lvlSeeds];
      }
    }
  };

  const requestSeedPlan = async (version: BlinkoVersion, seed: number): Promise<boolean> => {
    sourceSeedRef.current = seed; // remember for lazy per-level generation
    const requestId = ++planRequestIdRef.current;
    try {
      const plan = await seedPlanWorkerClient.requestPlan(version, seed);
      if (planRequestIdRef.current !== requestId) {
        return false;
      }
      applySeedPlan(plan);
      return true;
    } catch (_) {
      // fallback
      try {
        const plan = generateSeedPlan(version, seed);
        if (planRequestIdRef.current !== requestId) {
          return false;
        }
        applySeedPlan(plan);
        return true;
      } catch (fallbackError) {
        return false;
      }
    }
  };

  // Replay Setup
  const isReplayMode = useAtomValue(atomIsReplayMode);

  // Helper: Initialize Game Session
  const initializeGameSession = (
    betAmount: bigint,
    gameHardMode: boolean,
    version: BlinkoVersion,
    initialStatus: 'playing' | 'waiting' = 'playing'
  ) => {
    // Determine Level Config
    let config: LevelConfig;
    if (version === 'v1') {
      config = gameHardMode ? LEVEL_CONFIG_HARD_V1 : LEVEL_CONFIG_V1;
    } else {
      config = gameHardMode ? LEVEL_CONFIG_HARD_V2 : LEVEL_CONFIG_V2;
    }
    setLevelConfig(config);
    setGameVersion(version);

    // Sync State & Refs
    sessionBetAmountRef.current = betAmount;
    setWageredEth(betAmount);
    setUserInput(prev => ({ ...prev, tollAmount: formatEther(betAmount) }));
    setIsHardMode(gameHardMode);

    // Reset Validation/Tracking
    setRecentBins([]);
    setHoneyEarned(0);
    honeyAnimationPlayedLevelsRef.current.clear();
    setCollectHoneyTrigger(0);
    setTotalWinEth(0n);

    // Reset Theme
    setTheme('day');
    setBgmTheme('day');
    setShowMachinary(false);

    // Reset Engine State
    setGameState(initialStatus);
    setLevel(1);
    setAchievedLevel(1);
    setExperience(0);
    setBallsRemaining(LAUNCHES_PER_GAME);
    setTotalBallsUsed(0);
    setSpecialHitsCount(0);
    setSessionHits([]); // Clear session hits
  };

  const replayGame = async (gameId: string) => {
    if (startInProgressRef.current) return;
    startInProgressRef.current = true;
    setIsGamePreparing(true);

    try {
      const game = await serviceReplayGame(gameId);
      gameIdRef.current = game.gameId;

      const storedBet = BigInt(game.betAmount);
      const versionMode = checkVersionMode(game);
      initializeGameSession(
        storedBet,
        versionMode.isHardMode,
        versionMode.version,
        'waiting'
      );

      const derivedSeed = deriveSourceSeedFromCommitment(game.gameSeed as Hex, game.salt as Hex);
      if (!(await requestSeedPlan(versionMode.version, derivedSeed))) {
        throw new Error('Failed to generate replay seed plan');
      }
    } catch (e) {
      console.error('Replay Setup Failed', e);
      handleError('Replay Failed', e instanceof Error ? e : new Error('Unknown'));
    } finally {
      startInProgressRef.current = false;
      setIsGamePreparing(false);
    }
  };

  const verifyGame = async (gameId: string) => {
    if (startInProgressRef.current) return;
    startInProgressRef.current = true;
    setIsGamePreparing(true);

    try {
      const game = await serviceReplayGame(gameId);
      gameIdRef.current = game.gameId;

      const storedBet = BigInt(game.betAmount);
      const versionMode = checkVersionMode(game);
      initializeGameSession(
        storedBet,
        versionMode.isHardMode,
        versionMode.version,
        'waiting'
      );

      const derivedSeed = deriveSourceSeedFromCommitment(game.gameSeed as Hex, game.salt as Hex);
      if (!(await requestSeedPlan(versionMode.version, derivedSeed))) {
        throw new Error('Failed to generate verification seed plan');
      }

    } catch (e) {
      console.error('Verify Setup Failed', e);
      handleError('Verify Failed', e instanceof Error ? e : new Error('Unknown'));
    } finally {
      startInProgressRef.current = false;
      setIsGamePreparing(false);
    }
  };


  // Actions
  const startGame = async () => {
    if (startInProgressRef.current) {
      sound.play('error');
      return;
    }

    // Replay Mode Bypass
    if (isReplayMode) {
       setBallsRemaining(LAUNCHES_PER_GAME);
       setTotalBallsUsed(0);
       setTotalWinEth(0n);
       setActiveBallCount(0);
       setGameState('playing');
       return;
    }

    const isDemo = !address;

    startInProgressRef.current = true;
    setIsGamePreparing(true);

    try {
      if (!isDemo) {
        if (!isSigned) {
          throw new Error('need to sign in');
        }
        // Check Balance
        const ethBalance = getBalance(ZERO_ADDRESS).balance;
        if (ethBalance < betWei) {
          toast.info('You need to add funds to play')
          const profile = await profileCache.get(address);
          addFunds({
            name: profile.name || 'Me',
            address: profile.address,
          }, () => {
            console.log('addFunds success callback');
            mutateAllBalance();
          });
          return;
        }
      }
      if (activeBallCountRef.current > 0) {
        throw new Error('game already ongoing');
      }
      if (gameState !== 'waiting') {
        throw new Error('game already ongoing');
      }

      // Onchain Game Creation Flow
      if (!isDemo) {
        if (ballSeedPlanRef.current.length === 0) {
          
          const game = await serviceStartGame({
            player: address!,
            token: 'ETH',
            selectedToken: 'ETH',
            betAmount: betWei.toString(),
            hardMode: isHardMode,
            v2Mode: true,
          });
          gameIdRef.current = game.gameId;

          mutateAllBalance();

          const derived = deriveSourceSeedFromCommitment(game.gameSeed as Hex, game.salt as Hex);
          if (!(await requestSeedPlan('v2', derived))) {
            throw new Error('Failed to generate seed plan');
          }
        }
      } else {
        // Demo Flow
        const newSeed = getSecureSeed();
        if (!(await requestSeedPlan('v2', newSeed))) {
          throw new Error('demo seed failed');
        }
      }

      // Initialize Session
      initializeGameSession(
        parseEther(userInput.tollAmount || '0'),
        isHardMode,
        'v2',
        'playing'
      );

      if (isIgamingDemo) {
        const betUsd = parseFloat(userInput.tollAmount || '0') * 1000;
        setDemoBalance(prev => prev - betUsd);
      }
    } catch (e) {
      if (e instanceof Error) {
        handleError('Game Error', e);
      }
      sound.play('error');
    } finally {
      startInProgressRef.current = false;
      setIsGamePreparing(false);
    }
  };

  const resumeGame = async (gameId: string) => {
    if (startInProgressRef.current) return;
    startInProgressRef.current = true;
    setIsGamePreparing(true);

    try {
      const game = await serviceResumeGame(gameId);
      gameIdRef.current = game.gameId;

      const storedBet = BigInt(game.betAmount);
      const versionMode = checkVersionMode(game);
      initializeGameSession(
        storedBet,
        versionMode.isHardMode,
        versionMode.version,
        'playing'
      );

      toast.success('Resuming game session');

      const derived = deriveSourceSeedFromCommitment(game.gameSeed as Hex, game.salt as Hex);
      if (!(await requestSeedPlan(versionMode.version, derived))) {
        throw new Error('Failed to restore seed plan');
      }
    } catch (e) {
      console.error('Resume failed', e);
      handleError('Resume Failed', e instanceof Error ? e : new Error('Unknown'));
      setGameState('waiting');
    } finally {
      startInProgressRef.current = false;
      setIsGamePreparing(false);
    }
  };
  
  useEffect(() => {
    const gameId = searchParams.get('gameId');
    if (!gameId || gameId === 'undefined' || gameId === 'null' || !address) {
      return;
    }

    setSearchParams(prev => {
      prev.delete('gameId');
      return prev;
    }, { replace: true });
    resumeGame(gameId);
  }, [address, searchParams, setSearchParams]);

  const endGame = () => {
    const reached = calculateReachedLevel(experience);
    setAchievedLevel(reached);

    if (level < reached) {
      // Level Up
      const next = level + 1;
      // LAZY: generate this bonus level's ball seeds on demand (avoids eager ~82k-ball plan).
      ensureSeedsForLevel(next);
      setLevel(next);
      setBallsRemaining(LAUNCHES_PER_GAME);
      setTotalBallsUsed(0);
      setGameState('levelup');
      sound.play('levelUp');
      setTimeout(() => setGameState('playing'), 2000);
    } else {
      // Game Over or Max Level
      const isMax = level >= levelConfig.maxLevel;
      const endedInBonus = currentModeRef.current === 'bonus' || gameState === 'bonus';
      setGameState(isMax ? 'maxlevel' : 'gameover');

      if (isIgamingDemo) {
        const winEth = parseFloat(formatEther(totalWinEthRef.current));
        const winUsd = winEth * 1000;
        if (winUsd > 0) {
          setDemoBalance(prev => prev + winUsd);
        }
      }

      const runResultFlow = () => {
        if (address) {
          profileCache.mutate(address);
        }
        mutateAchievements();
        mutateLeaderboard();
        mutateAllBalance();

        // Clear seed plan to ensure next startGame triggers a new transaction
        if (!isReplayMode) {
          ballSeedPlanRef.current = [];
          setGameState('waiting');
        }
      };

      // LDW avoidance (Dixon 2010, Myles 2023): only fire the win-celebration modal when the
      // game NET >= stake. A bonus that returns less than the bet is a loss and must not be
      // dressed as a win. Sub-stake outcomes fall through to a quiet result flow.
      if (endedInBonus && totalWinEthRef.current >= sessionBetAmountRef.current) {
        const totalWinEth = totalWinEthRef.current;
        const ethValue = parseFloat(formatEther(totalWinEth));
        const usdValue = ethValue * ethPriceUsd;

        const sessionBet = sessionBetAmountRef.current;
        const multiplier = Number((totalWinEth * 10000n) / sessionBet) / 10000;

        setUiHidden(true);

        showModal(hideResult => (
          <ResultModal
            level={level}
            winAmount={usdValue}
            onComplete={() => {
              hideResult();
              if (!isReplayMode) {
                showModal(hideShare => (
                  <ShareModal
                    multiplier={multiplier} 
                    winAmount={usdValue}
                    gameId={gameIdRef.current!}
                    onDismiss={hideShare}
                  />
                ), {
                  hideOnEscapeKey: true, 
                  hideOnSpaceKey: true,
                  onHide: () => {
                    setUiHidden(false);
                    runResultFlow();
                  }
                });
              } else {
                setUiHidden(false);
                runResultFlow();
              }
            }}
          />
        ));
      } else {
        runResultFlow();
      }
    }
  };

  const dropBall = () => {
    if (gameState === 'waiting') {
      startGame();
      return;
    }
    if ((gameState !== 'playing' && gameState !== 'bonus') || ballsRemaining <= 0) {
      sound.play('error');
      return;
    }

    const multiplier = levelToMultiplier(level);
    const ballsToDrop = 1;

    const count = ballsToDrop * multiplier;

    // Guarantee this level's ball seeds exist regardless of how the level was reached
    // (bonus entry vs level-up both funnel here before any ball is pulled).
    ensureSeedsForLevel(level);

    const plan = ballSeedPlanRef.current;
    const start = ballSeedUsageRef.current;
    const seeds = [];
    if (plan.length && start < plan.length) {
      const end = Math.min(plan.length, start + count);
      for (let i = start; i < end; i++) {
        seeds.push(plan[i]);
      }
      ballSeedUsageRef.current = end;
    }

    if (seeds.length === 0) {
      sound.play('error');
      return;
    }

    setActiveBallCount(p => p + seeds.length);
    seeds.forEach(s => addBall(s));
    setBallsRemaining(p => p - 1);
    setTotalBallsUsed(p => p + 1);
  };

  const dropMultipleBalls = (requestedCount: number) => {
    const available = ballsRemainingRef.current; // access ref
    const limit = Math.min(requestedCount, available);
    ensureSeedsForLevel(levelRef.current); // guarantee current level's seeds exist
    for (let i = 0; i < limit; i++) {
      const multiplier = levelToMultiplier(levelRef.current);
      const seedsCount = multiplier;

      const plan = ballSeedPlanRef.current;
      const start = ballSeedUsageRef.current;
      const seeds = [];
      if (plan.length && start < plan.length) {
        const end = Math.min(plan.length, start + seedsCount);
        for (let k = start; k < end; k++) {
          seeds.push(plan[k]);
        }
        ballSeedUsageRef.current = end;
      }
      if (seeds.length > 0) {
        setActiveBallCount(p => p + seeds.length);
        seeds.forEach(s => addBall(s));
        setBallsRemaining(p => p - 1);
        setTotalBallsUsed(p => p + 1);
      }
    }
  };

  useEffect(() => {
    if ((gameState === 'playing' || gameState === 'bonus') && ballsRemaining === 0 && activeBallCount === 0) {
      endGame();
    }
  }, [gameState, ballsRemaining, activeBallCount, endGame]);

  return {
    canvasRef,
    canvasDimensions: dimensions,
    canvasScale: scale,
    actions: {
      startGame,
      dropBall,
      dropMultipleBalls,
      setCharacterState,
    },
    replayGame,
    verifyGame,
  };
};
